import React from 'react';
import RecraftGlyph from '../RecraftGlyph';
import { DamageSword } from './ResultPillIcons';
import CombatTooltip from '../GameTip/CombatTooltip';
import { PotassiumPill } from '../StatusTags/KcStatusPills';
import { isHtnDrugTooltipRowsEnabled } from '../../utils/envFlags';
import {
  htnDrugInfluenceGroups,
  potassiumShiftRows,
  kNetDirectionWord,
} from './HtnDrugInfluenceTooltip';
import { potassiumBandModel } from './potassiumBandModel';
import { isCombatTerminalPhase } from './playerbarPhase';

// Magnitude-free direction arrow shared by every HTN status-tag ledger (potassium
// shift, diuresis build, pulse shift) — ONE definition so a newly authored tag never
// starts half-broken. down = lowers / up = raises; colored by the
// .gt-shift-dir--{down,up} class via currentColor.
export function ShiftArrow({ direction }) {
  const up = direction === 'up';
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5l0 14" />
      {up ? <path d="M18 11l-6 -6" /> : <path d="M18 13l-6 6" />}
      {up ? <path d="M6 11l6 -6" /> : <path d="M6 13l6 6" />}
    </svg>
  );
}

// The "on a wrong answer" mark (✗) — the enemy stat grammar's trigger glyph.
function WrongGlyph() {
  return (
    <svg
      className="gt-stat__icon"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

// The damage sword token is the shared DamageSword from ResultPillIcons — ONE
// definition across the enemy projected hit (decode.jsx) and this tag (Edward
// 2026-07-22), so a newly authored tooltip never starts half-broken.

// The band-independent statement of the potassium mechanic, for surfaces that have no
// installed regimen and therefore no band to quote a probability against. Kept verbatim
// in sync with the drug row model (htnBlister `influences-potassium`.line).
//
// Edward 2026-07-27, authored: the toast states the EFFECT and nothing else. It carries
// no percentage and no damage number on purpose, because both are properties of the
// whole regimen's band and a single drug in a toast cannot resolve either. Same rule as
// the weaken magnitude: derive it or say less, never invent it.
const MECHANIC_LINE_BANDLESS = 'Chance to take damage on wrong answer.';

const REGION_LABELS = {
  severe_hypo: 'SEVERE HYPO',
  hypo: 'HYPO',
  normal: 'NORMAL',
  hyper: 'HYPER',
  severe_hyper: 'SEVERE HYPER',
};

// The tooltip speaks to the CURRENT serum state only, never a catalogue of both
// poles: balanced reads as no penalty, a deranged pole shows its live effect, and
// a deranged pole with the mechanic disabled reads honestly as monitoring-only.
//
// Reads its numbers off the MODEL rather than re-deriving them from the raw bite.
// It used to do its own arithmetic on `bites[0]`, which meant the tag could quote
// one number in its sentence and a different one in its statrow whenever the two
// derivations drifted (and skipped the model's fallback entirely when the wire
// went quiet). The prefix is the only thing this adds over `model.effectText`.
function currentEffectLine(model) {
  if (model.bandKey === 'balanced') {
    return `Balanced. ${model.effectText}`;
  }
  if (model.active) {
    const condition = model.bandKey === 'hypokalemic' ? 'Hypokalemia' : 'Hyperkalemia';
    return `${condition}. ${model.effectText}`;
  }
  return model.effectText;
}

// The combat effect on the K⁺ pill's row, in the enemy STAT grammar (authored
// design). Balanced → a muted "No penalty". An active deranged band → the
// canonical "On ✗ N% to take ⚔ M" statrow, numbers read from the model's own
// effectStat so they can never diverge from the band. Deranged with the toxicity
// mechanic disabled → the honest "Monitoring only" line.
// Returns { node, aria } so the host aria-label speaks the same effect in words.
function potassiumEffect(model, bitesEnabled) {
  if (model.bandKey === 'balanced') {
    return {
      node: (
        <span className="gt-stat gt-stat--join"><span className="gt-stat__v">No penalty</span></span>
      ),
      aria: 'No penalty',
    };
  }
  if (model.active && model.effectStat) {
    const { chancePct, magnitude } = model.effectStat;
    return {
      node: (
        <span className="gt-statrow">
          <span className="gt-stat gt-stat--join"><span className="gt-stat__v">On</span></span>
          <span className="gt-stat gt-stat--wrong" style={{ color: 'var(--dmg)' }}>
            <WrongGlyph />
          </span>
          <span className="gt-stat gt-stat--num"><span className="gt-stat__v">{`${chancePct}%`}</span></span>
          <span className="gt-stat gt-stat--join"><span className="gt-stat__v">to take</span></span>
          <span className="gt-stat gt-stat--dmg" style={{ color: 'var(--dmg)' }}>
            <DamageSword />
            <span className="gt-stat__v">{magnitude}</span>
          </span>
        </span>
      ),
      aria: `${chancePct}% chance to take ${magnitude} damage on a wrong answer`,
    };
  }
  if (!bitesEnabled) {
    return {
      node: <span className="gt-text">Monitoring only. Not active this fight.</span>,
      aria: 'Monitoring only, not active this fight',
    };
  }
  return {
    node: <span className="gt-text">Outside the safe band.</span>,
    aria: 'Serum potassium is outside the safe band',
  };
}

/**
 * THE potassium tooltip. Returns CombatTooltip props (everything except children) so
 * every surface hovering a K⁺ pill renders this one panel instead of reconstructing
 * it. Edward 2026-07-25: the drug toast had drifted to a bespoke two-line
 * `.kc-panel`, and the authoritative truth for these embedded tooltips is how the
 * status reads on hover in the combat rail.
 *
 * `bandless` (the drug toast) is the honest-scope switch. The band, the serum value
 * and the "N% to take M" effect are all resolved from the WHOLE regimen at combat
 * install, so a single drug in a toast cannot determine any of them. Rather than
 * fabricate a band, bandless keeps the rail's grammar and ledger but states the
 * mechanic qualitatively and omits the serum/band entirely. Same trap as the weaken
 * magnitude: derive it or say less, never invent it.
 */
export function htnPotassiumTooltipProps({
  net = 0,
  pole = 'balanced',
  serum = 4.2,
  region = 'normal',
  severity = 'safe',
  bites = [],
  bitesEnabled = false,
  activeEquippedEffects = [],
  bandless = false,
}) {
  const serumNumber = Number.isFinite(Number(serum)) ? Number(serum) : 4.2;
  const serumLabel = serumNumber.toFixed(2);
  const normalizedRegion = REGION_LABELS[region] ? region : 'normal';
  const statusLabel = REGION_LABELS[normalizedRegion];
  const n = Number.isFinite(Number(net)) ? Number(net) : 0;
  const sign = n > 0 ? `+${n}` : `${n}`;
  // Single source of truth for band identity + chroming + the flag-faithful effect
  // copy: region-first band collapse (pole is only a fallback for an unknown region),
  // so the pill/tone chroming can never diverge from the band the tooltip states.
  const model = potassiumBandModel({
    region: normalizedRegion,
    severity,
    pole,
    net: n,
    serum: serumNumber,
    bites,
    bitesEnabled,
  });
  const isHyper = model.bandKey === 'hyperkalemic';
  const isHypo = model.bandKey === 'hypokalemic';
  const tone = model.tone;
  const mechanicLine = currentEffectLine(model);
  // `kc-pill--banded` opts the combat K+ pill into band-toned chroming; --kc-accent
  // carries the current band tone (= the tooltip's --effect-color), so the pill's
  // colour tracks the tooltip scheme (hypo blue / balanced green / hyper red). The
  // drug-toast PotassiumTag has no --banded class, so it keeps its default identity.
  const pillClasses = [
    'kc-pill--banded',
    isHyper ? 'is-hyper' : '',
    isHypo ? 'is-hypo' : '',
    `is-${severity || 'safe'}`,
  ].filter(Boolean).join(' ');
  // Production/default builds retain the exact compact inline tooltip. Preprod's
  // authored-row flag opts into the per-band tailored mesh below.
  if (!isHtnDrugTooltipRowsEnabled()) {
    return {
      inline: true,
      color: bandless ? 'rgb(248, 113, 113)' : tone,
      name: bandless ? 'Potassium' : `Serum potassium · ${statusLabel}`,
      placement: 'above-left',
      icon: <RecraftGlyph slug="potassium" size={18} className="combat-tt__node-icon" />,
      sections: [{
        lines: bandless
          ? [MECHANIC_LINE_BANDLESS]
          : [
            `K ${serumLabel} · regimen shift ${sign}. Fixed for this fight.`,
            mechanicLine,
          ],
      }],
    };
  }

  // Drug-agnostic status tooltip in the enemy tooltips' STAT grammar (Edward's
  // 2026-07-22 authored design port-back). Compact, full-width: the K⁺ pill row
  // carries the combat effect on the RIGHT — "On ✗ N% to take ⚔ M" when a deranged
  // band is active, "No penalty" when balanced — then a "Shifted by" net + per-drug
  // direction ledger. The split header states the band (name │ band), so there is
  // no separate band-label row and no "Serum" / "regimen shift · fixed" filler.
  const potassiumGroups = htnDrugInfluenceGroups(activeEquippedEffects, 'potassium');
  const shiftRows = potassiumShiftRows(potassiumGroups);
  const netWord = kNetDirectionWord(n);
  const netToneClass = netWord === 'higher' ? 'up' : netWord === 'lower' ? 'down' : 'flat';
  // Bandless (drug toast): no regimen is installed, so there is no serum, no band and
  // no probability/magnitude to quote. The row keeps the rail's chassis but the effect
  // cell states the mechanic qualitatively.
  //
  // It also renders NO pill. The bandless surface is reached by hovering the potassium
  // tag inside a drug toast, so a pill in the panel was the hovered tag drawn a second
  // time, one row below itself, carrying the same authored range (Edward 2026-07-27:
  // "displays the status tag again which is redundant, it can just simply state the
  // effect"). The rail keeps its pill because there it is NOT a repeat: the rail tag
  // shows the band and the panel's pill shows the live serum value.
  const effect = bandless
    ? { node: <span className="gt-text">{MECHANIC_LINE_BANDLESS}</span>, aria: MECHANIC_LINE_BANDLESS }
    : potassiumEffect(model, bitesEnabled);
  const potassiumBody = (
    <span className="gt-inflicts-rows gt-potassium-rows">
      <span className={bandless ? 'gt-inflicts gt-inflicts--solo' : 'gt-inflicts'}>
        {!bandless && (
          <span className="gt-inflicts__l">
            <PotassiumPill readout={model.serumLabel} className={pillClasses} style={{ '--kc-accent': tone }} />
          </span>
        )}
        <span className="gt-inflicts__r">{effect.node}</span>
      </span>
      {shiftRows.length > 0 && (
        <>
          <span className="gt-inflicts gt-inflicts--shift">
            <span className="gt-inflicts__l"><span className="gt-text">Shifted by</span></span>
            <span className="gt-inflicts__r">
              <span className={`gt-shift-net gt-shift-net--${netToneClass}`}>
                <span className="gt-shift-net__k">net</span>
                <span className="gt-shift-net__v">{netWord}</span>
              </span>
            </span>
          </span>
          <span className="gt-shift-ledger">
            {shiftRows.map((rowItem) => (
              <span className="gt-shift-row" key={rowItem.slug}>
                <span className={`gt-shift-dir gt-shift-dir--${rowItem.direction}`}>
                  <ShiftArrow direction={rowItem.direction} />
                </span>
                <span className="gt-shift-name">{rowItem.drugName}</span>
                {rowItem.classLabel ? (
                  <span className="gt-shift-class">{rowItem.classLabel}</span>
                ) : null}
              </span>
            ))}
          </span>
        </>
      )}
    </span>
  );
  const ariaLabel = [
    bandless ? 'Potassium' : `Serum potassium · ${statusLabel}`,
    bandless ? '' : `K ${model.serumLabel}, regimen shift ${model.shiftLabel}, fixed for this fight`,
    effect.aria,
    shiftRows.length
      ? `Shifted by ${shiftRows
          .map((rowItem) => `${rowItem.drugName} ${rowItem.direction === 'up' ? 'raises' : 'lowers'} potassium`)
          .join(', ')}. Net ${netWord}`
      : '',
  ].filter(Boolean).join('. ');

  return {
    color: bandless ? 'rgb(248, 113, 113)' : tone,
    name: bandless ? 'Potassium' : 'Serum potassium',
    // No band is knowable without an installed regimen, so bandless carries no
    // subtitle rather than asserting one.
    subtitle: bandless ? undefined : statusLabel,
    placement: 'above-left',
    icon: <RecraftGlyph slug="potassium" size={18} className="combat-tt__node-icon" />,
    sections: [{ content: potassiumBody }],
    ariaLabel,
  };
}

// HTN potassium is regimen-derived once at combat install. The compact pill
// renders that persisted serum value and never animates or drifts during the fight.
function PlayerPotassiumTag({
  net = 0,
  pole = 'balanced',
  serum = 4.2,
  region = 'normal',
  severity = 'safe',
  bites = [],
  bitesEnabled = false,
  activeEquippedEffects = [],
  visible = false,
  phase,
}) {
  if (!visible || isCombatTerminalPhase(phase)) {
    return null;
  }
  const serumNumber = Number.isFinite(Number(serum)) ? Number(serum) : 4.2;
  const serumLabel = serumNumber.toFixed(2);
  const normalizedRegion = REGION_LABELS[region] ? region : 'normal';
  const statusLabel = REGION_LABELS[normalizedRegion];
  const n = Number.isFinite(Number(net)) ? Number(net) : 0;
  const sign = n > 0 ? `+${n}` : `${n}`;
  const model = potassiumBandModel({
    region: normalizedRegion,
    severity,
    pole,
    net: n,
    serum: serumNumber,
    bites,
    bitesEnabled,
  });
  const pillClasses = [
    'kc-pill--banded',
    model.bandKey === 'hyperkalemic' ? 'is-hyper' : '',
    model.bandKey === 'hypokalemic' ? 'is-hypo' : '',
    `is-${severity || 'safe'}`,
  ].filter(Boolean).join(' ');
  const trigger = (
    <PotassiumPill
      readout={serumLabel}
      className={pillClasses}
      style={{ '--kc-accent': model.tone }}
      aria-label={`Serum potassium ${serumLabel}, ${statusLabel.toLowerCase()}, fixed regimen shift ${sign}`}
    />
  );
  return (
    <CombatTooltip
      {...htnPotassiumTooltipProps({
        net: n, pole, serum: serumNumber, region: normalizedRegion, severity, bites, bitesEnabled, activeEquippedEffects,
      })}
    >
      {trigger}
    </CombatTooltip>
  );
}

export default PlayerPotassiumTag;
